import { useMemo } from "react";
import sortByFn from "lodash/sortBy";
import get from "lodash/get";
import { GroupByConfig } from "../types.ts";

export type GroupedData<Data> = Array<{
  key: string | number;
  label: string;
  items: Data[];
}>;

type Params<Data> = {
  groupBy?: GroupByConfig<Data>;
  data: Data[];
  sortField?: string;
  sortDirection?: TableSortDirection;
};

export function useGroupedData<Data>({
  groupBy,
  data,
  sortField,
  sortDirection,
}: Params<Data>): GroupedData<Data> | null {
  return useMemo(() => {
    if (!groupBy) return null;
    const groups = new Map<
      string | number,
      { key: string | number; label: string; items: Data[] }
    >();

    data.forEach((item) => {
      const key = groupBy.getKey(item);
      const existing = groups.get(key);
      if (existing) {
        existing.items.push(item);
      } else {
        groups.set(key, { key, label: "", items: [item] });
      }
    });

    groups.forEach((group) => {
      group.label = groupBy.getLabel(group.key, group.items);
    });

    const result = Array.from(groups.values());
    if (groupBy.sortGroups) {
      result.sort((a, b) => groupBy.sortGroups!(a.key, b.key));
    }

    if (sortField) {
      result.forEach((group) => {
        const sorted = sortByFn(group.items, (item) => {
          const value = get(item, sortField);
          const nan = Number.isNaN(Number(value));
          return (nan ? value : Number(value)) || "";
        });
        group.items = sortDirection === "DESC" ? sorted.reverse() : sorted;
      });
    }

    return result;
  }, [groupBy, data, sortField, sortDirection]);
}
